import { Box, Button, Divider, FormLabel, Pagination, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useFormik } from 'formik';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import utilityApi from '~/apis/utility';
import { AddNewIcon, ExportFillIcon } from '~/assets/icons';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { cast, clearInputFilter, exportExcel, handleApiError, notify } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';
import AgentTable from './component/AgentTable';
import styles from './Agent.module.scss';
import { IAccount, IGetAllParams } from '~/utils/interface/account';
import accountApi from '~/apis/account';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import dayjs from 'dayjs';
import { ROLE_AGENT } from '~/utils/constants/account';
import DetailAgentDialog from './component/detail-agent-dialog/DetailAgentDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, DIALOG_MODE_VIEW } from '~/utils/constants/common';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import TranferDeviceDialog from './component/tranfer-device-dialog/TranferDeviceDialog';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import EditTechnicianDialog from '../technicians/component/EditTechnicianDialog';

dayjs.extend(customParseFormat);

const cx = classNames.bind(styles);

const initialValues: IGetAllParams = {
  agent_level: '',
  distributor: '',
  date_to: '',
  order_by: {
    field: '',
    type: '',
  },
  page_index: 1,
  page_size: 10,
};

const Agent = () => {
  const { t } = useTranslation('admin/agent');
  const [data, setAccount] = useState<IAccount[]>([]);
  const [nppOptions, setNppOptions] = useState<IMasterDataItem[]>([]);
  const [agentLevelOptions, setAgentLevelOptions] = useState<IMasterDataItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [refreshData, setRefreshData] = useState(false);
  const [isOpenDetailAgentDialog, setIsOpenDetailAgentDialog] = useState<boolean>(false);
  const [agent, setAgent] = useState<IAccount>();
  const [agentDialogMode, setAgentDialogMode] = useState<string>();
  const [isOpenDeleteAgentDialog, setIsOpenDeleteAgentDialog] = useState<boolean>(false);
  const [agentIds, setAgentIds] = useState<Array<string>>([]);
  const [isOpenTranferDeviceDialog, setIsOpenTranferDeviceDialog] = useState<boolean>(false);
  const [isOpenUpdateTechnicianDialog, setIsOpenUpdateTechnicianDialog] = useState<boolean>(false);

  useEffect(() => {
    if (refreshData) {
      handleGetAgents();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  useEffect(() => {
    (async () => {
      try {
        const response = await utilityApi.getListDistributors();
        setNppOptions(
          response.data.map((item) => {
            return {
              id: item.username,
              name: item.username + ' - ' + item.name,
              data0: item.name,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
    (async () => {
      try {
        const response = await utilityApi.getAgentLevels();
        setAgentLevelOptions(
          response.data.map((item) => {
            return {
              id: String(item.level_id),
              name: item.level_name,
              data0: item.level_name,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, []);

  const form = useFormik<IGetAllParams>({
    initialValues,
    onSubmit: async () => {
      handleGetAgents();
    },
  });

  useEffect(() => {
    handleGetAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size]);

  useEffect(() => {
    form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values]);

  const getAgents = async (overrideParams = {}) => {
    try {
      return (
        await accountApi.getByRoleList(ROLE_AGENT, {
          ...form.values,
          agent_level: form.values.agent_level ? Number(form.values.agent_level) : 0,
          distributor_level: form.values.distributor_level ? Number(form.values.distributor_level) : 0,
          created_at: form.values.created_at
            ? dayjs(form.values.created_at, 'DD/MM/YYYY').format('YYYY-MM-DD')
            : undefined,
          ...overrideParams,
        })
      ).data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, data: [] };
    }
  };

  const handleGetAgents = async () => {
    const response = await getAgents();
    setTotalItems(response.total);
    setAccount(response.data);
  };

  const handleSetIsOpenEditAgentDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenDetailAgentDialog(isOpen);
    if (needRefresh) {
      handleGetAgents();
    }
  };

  const handleDeleteAgent = async (needRefresh: boolean = false) => {
    try {
      if (agent) {
        await accountApi.deleteByRole({ role: ROLE_AGENT, username: agent.username });
        if (needRefresh) {
          handleGetAgents();
        }
        notify('success', t('deleteAgentDialog.deleteSuccess'));
        setIsOpenDeleteAgentDialog(false);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCheckAll = async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const res = await getAgents({ page_index: 1, page_size: totalItems });
      setAgentIds(res.data?.map((account: IAccount) => cast(account.username)));
    } else {
      setAgentIds([]);
    }
  };

  const handleCheck = async (row: IAccount, checked: boolean) => {
    if (checked) {
      setAgentIds((prev) => [...(prev ?? []), ...[cast(row.username)]]);
    } else {
      setAgentIds(agentIds.filter((account) => account !== row.username));
    }
  };

  const exportFile = async () => {
    if (!agentIds.length) return notify('error', t('emptyExportFile'));
    const response = await getAgents({ page_index: 1, page_size: totalItems });

    let res = response.data;
    if (agentIds.length) {
      const filterSet = new Set(agentIds);
      res = response.data?.filter((item) => filterSet.has(cast(item.username)));
      if (!res.length) {
        setAgentIds([]);
        res = response.data;
      }
    }

    const columns = [
      { header: t('table.username'), key: 'username', width: 20 },
      { header: t('table.name'), key: 'name', width: 15 },
      { header: t('table.address'), key: 'address', width: 20 },
      { header: t('table.representative'), key: 'representative', width: 15 },
      { header: t('table.phone'), key: 'phone', width: 15 },
      { header: t('table.email'), key: 'email', width: 15 },
      { header: t('table.totalDevices'), key: 'total_devices' },
      { header: t('table.totalUsers'), key: 'total_users' },
      { header: t('table.totalTechnicians'), key: 'total_technicians' },
      { header: t('table.createdAt'), key: 'created_at', width: 20 },
      { header: t('table.agentLevel'), key: 'agent_level', width: 20 },
    ];

    const data = res?.map((row, index) => ({
      ...row,
      no: index + 1,
      agent_level: agentLevelOptions?.find((item) => Number(item.id) === Number(row.agent_level))?.name,
    }));

    exportExcel(columns, data, t('fileName'));
  };

  return (
    <Box className={cx('w-full', 'equipment-screen')}>
      <Box className={cx('box-container')}>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='distributor'>{t('npp')}</FormLabel>
            <BaseSelect
              options={nppOptions}
              value={form.values.distributor}
              placeholder={t('select')}
              name='distributor'
              isSearchEnable
              onChange={form.handleChange}
              displayInvalidValue
              isDisplayAllOption
            />
          </Box>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='agent'>{t('agentLevel')}</FormLabel>
            <BaseSelect
              options={agentLevelOptions}
              value={form.values.agent_level}
              placeholder={t('select')}
              name='agent_level'
              isSearchEnable
              onChange={form.handleChange}
              displayInvalidValue
              isDisplayAllOption
              defaultValue={0}
            />
          </Box>
        </Box>
        <Box className='flex flex-row items-center gap-[20px]'>
          <FormLabel htmlFor='date_to'>{t('createdDate')}</FormLabel>
          <Box className={cx('column-text-field')}>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='date_from'>{t('start')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={form.values.date_from}
                dateFormat='YYYY-MM-DD'
                onChange={(date) => form.setFieldValue('date_from', dayjs(date).format('YYYY-MM-DD'))}
                placeholder={t('select')}
                calendarProps={form.values.date_to ? { maxDate: new Date(form.values.date_to) } : {}}
              />
            </Box>
            <Box className={cx('row-text-field')}>
              <FormLabel htmlFor='date_to'>{t('end')}</FormLabel>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={form.values.date_to}
                dateFormat='YYYY-MM-DD'
                onChange={(date) => form.setFieldValue('date_to', dayjs(date).format('YYYY-MM-DD'))}
                placeholder={t('select')}
                calendarProps={form.values.date_from ? { minDate: new Date(form.values.date_from) } : {}}
              />
            </Box>
          </Box>
        </Box>
        <Box className=' text-center'>
          <Button
            className='bg-[#ED1002] rounded-[20px] text-white w-[130px] h-[35px] hover:bg-[#ED1002] hover:opacity-80'
            onClick={() => {
              clearInputFilter();
              form.resetForm();
            }}
          >
            {t('clearFilter')}
          </Button>
        </Box>
      </Box>
      <Divider
        sx={{
          borderColor: '#005F73',
          borderBottomWidth: 1,
          width: '100%',
          my: '14px',
        }}
      ></Divider>
      <Box className={cx('table-container')}>
        <Box className='bg-[#ED1002] flex py-2 px-5 justify-between items-center'>
          <Typography className='text-[16px] font-bold text-[white]'>{t('title')}</Typography>
          <Box className='flex pr-10'>
            <Button
              startIcon={<AddNewIcon />}
              className='text-white'
              onClick={() => {
                setAgentDialogMode(DIALOG_MODE_CREATE);
                setIsOpenDetailAgentDialog(true);
              }}
            >
              {t('addNew')}
            </Button>

            <Button startIcon={<ExportFillIcon />} className='text-white' onClick={exportFile}>
              {t('fileExport')}
            </Button>
          </Box>
        </Box>
        <AgentTable
          pageSize={form.values.page_size ?? 10}
          data={data}
          agentLevels={agentLevelOptions}
          form={form}
          onEdit={(row) => {
            setAgent(row);
            setIsOpenDetailAgentDialog(true);
            setAgentDialogMode(DIALOG_MODE_EDIT);
          }}
          onDetail={(row) => {
            setAgent(row);
            setIsOpenDetailAgentDialog(true);
            setAgentDialogMode(DIALOG_MODE_VIEW);
          }}
          onAddTechnician={(row) => {
            setAgent(row);
            setIsOpenUpdateTechnicianDialog(true);
          }}
          onTranfer={(row) => {
            setAgent(row);
            setIsOpenTranferDeviceDialog(true);
          }}
          onDelete={(row) => {
            setAgent(row);
            setIsOpenDeleteAgentDialog(true);
          }}
          setRefreshData={setRefreshData}
          agentIds={agentIds}
          handleCheck={handleCheck}
          handleCheckAll={handleCheckAll}
        />
      </Box>
      <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
        <Typography>
          {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span> {t('record')}
        </Typography>
        <Box className='w-[150px]'>
          <BaseSelect
            options={[
              { id: 10, name: '10' },
              { id: 20, name: '20' },
              { id: 50, name: '50' },
              { id: 100, name: '100' },
              { id: 250, name: '250' },
              { id: 500, name: '500' },
            ]}
            value={form.values.page_size}
            onChange={(e) => {
              form.setFieldValue('page_size', e.target.value);
              form.setFieldValue('page_index', 1);
            }}
            renderSelected={(option: any) => (
              <Typography className='px-2'>
                {option.name}/{t('page').toLowerCase()}
              </Typography>
            )}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
          variant='outlined'
          page={form.values.page_index ?? 1}
          onChange={(e, value: number) => {
            form.setFieldValue('page_index', value);
          }}
        />
        <Box className='flex items-center gap-3'>
          <Typography>{t('page')}</Typography>
          <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>{form.values.page_index}</Box>
        </Box>
      </Box>
      {isOpenDetailAgentDialog ? (
        <DetailAgentDialog
          open={isOpenDetailAgentDialog}
          setOpen={handleSetIsOpenEditAgentDialog}
          data={agent}
          mode={agentDialogMode}
        />
      ) : null}

      {isOpenUpdateTechnicianDialog ? (
        <EditTechnicianDialog
          open={isOpenUpdateTechnicianDialog}
          setOpen={(open) => setIsOpenUpdateTechnicianDialog(open)}
          mode={DIALOG_MODE_CREATE}
          parent_username={agent?.username}
        />
      ) : null}
      <ConfirmDialog
        open={isOpenDeleteAgentDialog}
        title={t('deleteAgentDialog.title')}
        description={t('deleteAgentDialog.description', {
          name: agent?.name,
        })}
        onConfirm={() => handleDeleteAgent(true)}
        onCancel={() => setIsOpenDeleteAgentDialog(false)}
      />
      <TranferDeviceDialog open={isOpenTranferDeviceDialog} setOpen={setIsOpenTranferDeviceDialog} data={agent} />
    </Box>
  );
};

export default memo(Agent);
